import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import type { UsAddress, UsStateEnum } from '@/lib/heyapi/types.gen';

interface AttachedFormsForAGivenTransactionNumberProps<T> {
  attachedForms: T[];
  isTimeTraveling: boolean;
  transactionNumber?: number | null;
}

/**
 * Filters a list of attached forms to those relevant for a given transaction number.
 *
 * In time-travel mode the filter is an exact match: only forms whose
 * `transaction_number` equals `transactionNumber` are included. In live mode it
 * is cumulative: forms at or below `transactionNumber` are included.
 *
 * Both branches short-circuit to `false` when either `transaction_number` or
 * `transactionNumber` is not a number.
 *
 * @param attachedForms - Array of form objects, each carrying a numeric `transaction_number`.
 * @param isTimeTraveling - When true, applies the exact-match (historical snapshot) branch.
 * @param transactionNumber - The reference transaction number to filter against.
 * @returns The filtered subset of `attachedForms`.
 */
export const attachedFormsForAGivenTransactionNumber = <
  T extends { transaction_number: number },
>({
  attachedForms,
  isTimeTraveling,
  transactionNumber,
}: AttachedFormsForAGivenTransactionNumberProps<T>) => {
  return attachedForms.filter((form) => {
    if (
      typeof form.transaction_number !== 'number' ||
      typeof transactionNumber !== 'number'
    ) {
      return false;
    }
    if (isTimeTraveling) {
      return form.transaction_number === transactionNumber;
    }
    return form.transaction_number <= transactionNumber;
  });
};

export function useDebouncedValue<Type>(
  val: Type,
  delay: number
): Type | undefined {
  const inputValue = JSON.stringify(val);
  const [debouncedValue, setDebouncedValue] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue, delay]);

  if (debouncedValue) {
    return JSON.parse(debouncedValue);
  }
  // return undefined;
}

// TODO: consider updating to named arguments
export const useDetermineVisibility = (
  dependsOn: string | number = '',
  compareWith: string | string[],
  mode: 'exclude' | 'compare' = 'compare'
) => {
  const [show, setShow] = useState<boolean>(false);

  const dependsOnString = dependsOn.toString();

  useEffect(() => {
    if (mode === 'exclude') {
      if (compareWith.includes(dependsOnString)) {
        setShow(false);
      } else {
        setShow(true);
      }
    } else if (mode === 'compare') {
      if (dependsOnString === compareWith) {
        setShow(true);
      } else {
        setShow(false);
      }
    }
  }, [dependsOnString, show, setShow, compareWith, mode]);

  return [show];
};

type Address = {
  line1: string[] | string;
  line2: string;
  city: string;
  county: string;
  zipCode: string;
  state: UsStateEnum;
  formatted: string;
};

// Types for parseGooglePlaceResult
export interface CityOption {
  type: string;
  value: string;
}

export interface ParsedGooglePlaceResult {
  line1: string;
  line2: string;
  city: string | undefined;
  cityOptions: CityOption[];
  county: string | undefined;
  state: UsStateEnum | undefined;
  zipCode: string | undefined;
  formatted: string | undefined;
  lat: number | undefined;
  lng: number | undefined;
}

const CITY_TYPES = [
  'locality',
  'sublocality',
  'postal_town',
  'administrative_area_level_3',
  'neighborhood',
];

export const parseGooglePlaceResult = (
  place: google.maps.places.PlaceResult
): ParsedGooglePlaceResult => {
  const addressComponents = place.address_components ?? [];

  // Build line1 from street_number + route
  let streetNumber = '';
  let route = '';
  let line2 = '';
  let zipCode: string | undefined;
  let state: UsStateEnum | undefined;
  let county: string | undefined;

  // Collect all city options
  const cityOptions: CityOption[] = [];

  addressComponents.forEach(({ long_name, short_name, types }) => {
    if (types.includes('street_number')) {
      streetNumber = long_name;
    }

    if (types.includes('route')) {
      route = long_name;
    }

    if (types.includes('subpremise')) {
      line2 = long_name;
    }

    if (types.includes('postal_code')) {
      zipCode = long_name;
    }

    if (types.includes('administrative_area_level_1')) {
      state = short_name as UsStateEnum;
    } else if (
      types.includes('country') &&
      (short_name === 'PR' || short_name === 'VI')
    ) {
      state = short_name as UsStateEnum;
    }

    if (types.includes('administrative_area_level_2')) {
      county = long_name;
    }

    // Collect all city type options
    for (const cityType of CITY_TYPES) {
      if (types.includes(cityType)) {
        cityOptions.push({ type: cityType, value: long_name });
      }
    }
  });

  const line1 = [streetNumber, route].filter(Boolean).join(' ');

  // If only one city option, use it directly
  const city = cityOptions.length === 1 ? cityOptions[0].value : undefined;

  return {
    line1,
    line2,
    city,
    cityOptions,
    county,
    state,
    zipCode,
    lat: place.geometry?.location?.lat() ?? undefined,
    lng: place.geometry?.location?.lng() ?? undefined,
    formatted: place.formatted_address,
  };
};

/**
 * Finds the first address component that matches any of the priority types.
 * Iterates through priorityTypes in order and returns the long_name of the first match.
 *
 * @param priorityTypes - Array of Google address component types in priority order
 * @param addressComponents - Array of Google geocoder address components to search
 * @returns The long_name of the first matching component, or undefined if no match
 */
export const pickByPriority = (
  priorityTypes: string[],
  addressComponents: google.maps.GeocoderAddressComponent[]
) => {
  for (const type of priorityTypes) {
    const googleAddress = addressComponents.find((c) => c.types.includes(type));
    if (googleAddress) return googleAddress.long_name;
  }
  return undefined;
};

export const getFormattedGooglePlaceResults = (
  e: google.maps.places.PlaceResult
) => {
  const res: Partial<Address & { formatted: string; line1: string }> = {
    formatted: e.formatted_address,
  };

  const addressComponents = e.address_components ?? [];

  addressComponents.forEach(({ long_name, short_name, types }) => {
    if (types.includes('street_number') && !res?.line1) {
      res.line1 = [long_name].join(' ');
    }

    if (types.includes('route')) {
      res.line1 = `${res.line1 || ''} ${long_name}`;
    }
    if (types.includes('subpremise')) {
      res.line2 = long_name;
    }

    if (types.includes('postal_code')) {
      res.zipCode = long_name;
    }

    if (types.includes('administrative_area_level_1')) {
      res.state = short_name as UsStateEnum;
    } else if (
      types.includes('country') &&
      (short_name === 'PR' || short_name === 'VI')
    ) {
      // Puerto Rico & Virgin Islands are considered as separate countries & not
      // administrative levels. All territories will need to use this field
      res.state = short_name as UsStateEnum;
    }

    if (types.includes('administrative_area_level_2')) {
      res.county = long_name;
    }
  });

  // City with priority: locality > sublocality > administrative_area_level_3
  res.city = pickByPriority(
    ['locality', 'sublocality', 'administrative_area_level_3'],
    addressComponents
  );

  return res;
};

export const getDisplayAddressFromAddress = ({
  address,
  fallbackAddress = 'No Address',
}: {
  address: UsAddress | undefined | null;
  fallbackAddress?: string;
}): string => {
  let displayAddress = fallbackAddress;
  if (address) {
    const { line1, line2, city, state, zipCode } = address;
    displayAddress = [line1, line2, city, state, zipCode]
      .map((v) => v?.trim())
      .filter((v) => v)
      .join(', ');
  }

  if (!displayAddress) {
    displayAddress = fallbackAddress;
  }

  return displayAddress;
};

// format as currency
export const formatAsCurrency = (num: string | number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(num));
};

export const formatAsPercentage = (num: string | number) => {
  return Number(Number(num) / 100).toLocaleString('en-US', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
};

export const formatAsNumber = (num: string | number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(Number(num));
};

export type NumericMask = 'currency' | 'percentage' | 'number';

/**
 * Applies a display mask to a form value. Shared by every non-editable
 * rendering of a field (`ReadOnlyField`, `AutoFilled`) so a value reads the
 * same in quote status as it does after issuance.
 */
export const formatMasked = (value: ReactNode, mask?: NumericMask) => {
  const canFormat =
    (typeof value === 'string' || typeof value === 'number') && value !== '';

  if (mask === 'currency') {
    return canFormat ? formatAsCurrency(Number(value)) : '';
  }

  if (mask === 'percentage') {
    return canFormat ? formatAsPercentage(Number(value)) : '';
  }

  if (mask === 'number') {
    return canFormat ? formatAsNumber(Number(value)) : '';
  }

  return (!mask && value) || '';
};

/**
 * Example how to get all labels
 * const labels = FirstNamedInsuredTypeSchema.options.map((v) => FirstNamedInsuredTypeToLabel[v as FirstNamedInsuredType]);
 * */

// Think about how to use this function to get label for watch conditions in forms
export const getLabel = (key: string) => {
  const labels: Record<string, string> = {
    'Large Group': 'Large Group (10 Or More Dentists)',
    'Dental Association': 'Dental Association, Society Or Testing Board',
    'Volunteer Only': 'Solo Dentist, Volunteer Only',
    'Faculty Only': 'Solo Dentist, Faculty Only (Non Intramural)',
    Moonlighting: 'Solo Dentist, Moonlighting',
    'New Grad': '2 Year New Grad Policy',
    ASIC: 'ASPEN SPECIALTY INSURANCE COMPANY (ASIC)',
    AAIC: 'ASPEN AMERICAN INSURANCE COMPAY (AAIC)',
    Corporation: 'Corporation',
    Individual: 'Individual',
    LLC: 'LLC',
    LLP: 'LLP',
    Other: 'Other',
    Partnership: 'Partnership',
    'PC/PA': 'PC/PA',
  };

  return labels[key] || key;
};

export const getMappedOptions = (options: string[]) => {
  return options.map((key) => {
    const label = getLabel(key);
    return {
      value: key,
      label,
      isFound: key !== label,
    };
  });
};
